import React from 'react';
import Chip from '../common/Chip';
import { buildThumbnailUrl } from '../../utils/thumbnailUrl';
import {
    BAND_LABELS, describeEvidence, regionOverlay, timeSpanOverlay,
} from '../../utils/reverseSearchFormat.js';

/**
 * One reverse-search result.
 *
 * Shared by the "Similar" tab and the drop-a-file results view. It was
 * duplicated across the two while only images existed, and adding the audio
 * span made the duplication expensive in the way duplication usually becomes
 * expensive: the two copies would have started disagreeing about what a match
 * looks like, which is precisely the thing a user reads as unreliability.
 *
 * The band chip is contextual (`showBand`): an ungrouped list needs it, and the
 * results view — which groups by band and puts it in the heading — does not.
 *
 * ── The one rule ────────────────────────────────────────────────────────────
 * **A measurement and a guess must never look the same.** Stage 2 produces
 * evidence it actually measured — a region located by cross-correlation, a time
 * span that hundreds of spectral landmarks agree on — and that draws SOLID.
 * Stage 1 can only say which fragment matched, which is a coarse bucket rather
 * than a location, and that draws DASHED. The distinction is the whole reason
 * showing the evidence is worth anything: the user can look at it and judge.
 */
const MatchCard = ({ hit, onOpen, showBand = true }) => {
    const band = BAND_LABELS[hit.band] || BAND_LABELS.related;
    const region = regionOverlay(hit);
    const span = timeSpanOverlay(hit);
    const file = hit.file || {};
    const evidence = (hit.evidence || []).map(describeEvidence).filter(Boolean);

    return (
        <button
            type="button"
            className={`sa-card ${hit.verified ? 'is-verified' : ''}`.trim()}
            onClick={() => onOpen?.(hit.fileId)}
            title={evidence.length ? `${file.name}\n${evidence.join('\n')}` : file.name}
        >
            <div className="sa-thumb">
                <img src={buildThumbnailUrl({ id: hit.fileId })} alt="" loading="lazy" />
                {region && (
                    <span
                        className={`sa-region ${region.verified ? 'is-verified' : 'is-hint'}`}
                        style={{
                            left: `${region.rect.x * 100}%`,
                            top: `${region.rect.y * 100}%`,
                            width: `${region.rect.w * 100}%`,
                            height: `${region.rect.h * 100}%`,
                        }}
                        aria-hidden="true"
                    />
                )}
            </div>

            {/* The audio analogue of the region overlay: the whole track as a
                strip, with the matched span marked on it. An audio file's
                thumbnail is a generic icon, so a rectangle drawn over it would
                mean nothing — this puts the "where" somewhere it can mean
                something. */}
            {span && (
                <span className="sa-timespan" aria-hidden="true">
                    <span
                        className={`sa-timespan-mark ${span.verified ? 'is-verified' : 'is-hint'}`}
                        style={{ left: `${span.start * 100}%`, width: `${span.width * 100}%` }}
                    />
                </span>
            )}

            <div className="sa-meta">
                <span className="sa-name">{file.name || hit.fileId}</span>
                <span className="sa-chips">
                    {/* The band is the CLAIM ("Near-identical"); the evidence is
                        why ("Visually identical"). Both belong on the card in an
                        ungrouped list — but the results view groups BY band and
                        puts it in the heading, where repeating it on every card
                        below would be noise. Hence the prop rather than a rule.
                        Either way a card is never left with nothing to say. */}
                    {(showBand || evidence.length === 0) && (
                        <Chip variant={band.variant} size="sm">{band.label}</Chip>
                    )}
                    {evidence.slice(0, 2).map((text) => (
                        <Chip key={text} variant="neutral" size="sm">{text}</Chip>
                    ))}
                </span>
            </div>
        </button>
    );
};

export default MatchCard;
